// Notification sound utilities for real-time alerts

class NotificationSound {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    localStorage.setItem("notification_sound_enabled", String(enabled));
  }

  isEnabled(): boolean {
    const stored = localStorage.getItem("notification_sound_enabled");
    if (stored !== null) {
      this.enabled = stored === "true";
    }
    return this.enabled;
  }

  async playNotification(type: "success" | "warning" | "alert" = "success") {
    if (!this.isEnabled()) return;

    try {
      const ctx = this.getAudioContext();
      
      // Resume context if suspended (required for user interaction)
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Different tones for different notification types
      const frequencies = {
        success: [523.25, 659.25, 783.99], // C5, E5, G5 - pleasant chord
        warning: [440, 554.37], // A4, C#5 - attention
        alert: [880, 1046.50, 880], // A5, C6, A5 - urgent
      };

      const freqs = frequencies[type];
      const duration = type === "alert" ? 0.15 : 0.12;

      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration * freqs.length);

      let time = ctx.currentTime;
      for (const freq of freqs) {
        oscillator.frequency.setValueAtTime(freq, time);
        time += duration;
      }

      oscillator.type = "sine";
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration * freqs.length);
    } catch (error) {
      console.error("Error playing notification sound:", error);
    }
  }

  async playTableStatusChange() {
    await this.playNotification("success");
  }

  async playCommandStatusChange() {
    await this.playNotification("success");
  }

  async playNewOrder() {
    await this.playNotification("alert");
  }

  async playLowStock() {
    await this.playNotification("warning");
  }

  async playPendingPrintJobs() {
    await this.playNotification("warning");
  }
}

export const notificationSound = new NotificationSound();
